import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { format, isValid, parseISO } from 'date-fns';
import { Observable } from 'rxjs';
import { QcResponseGrid } from 'src/app/shared/models/grid.model';
import { IS_LOAD, IS_SEARCH } from 'src/app/shared/models/load.models';
import { EnvironmentFacadeService } from 'src/app/shared/services/environment.facade.service';
import { CampaignSearchLiteralsFacadeService } from 'src/app/shared/services/literals/campaign-search.literals.facade.service';
import { PaginatorLiteralsFacadeService } from 'src/app/shared/services/literals/paginator.literals.facade.service';
import { PaginatorFacadeService } from 'src/app/shared/services/paginator.facade.service';
import { CampaignSearchCategory, CampaignSearchSchema, Country, StaticData } from '../models/campaign-search.model';
import { CampaignSearchFacadeService } from './campaign-search.facade.service';

@Injectable({
  providedIn: 'root',
})
export class CampaignSearchService {
  constructor(
    private http: HttpClient,
    private environmentFacade: EnvironmentFacadeService,
    private facade: CampaignSearchFacadeService,
    private paginatorFacade: PaginatorFacadeService,
    private paginatorLiterals: PaginatorLiteralsFacadeService,
    private campaignLiterals: CampaignSearchLiteralsFacadeService,
  ) {}

  public getCategories(): Observable<CampaignSearchCategory[]> {
    return this.http.get<CampaignSearchCategory[]>(
      `${this.environmentFacade.getEnvironment()}/rest/v1/campaign-properties/categories`,
      {
        withCredentials: true,
        context: new HttpContext().set(IS_LOAD, true),
      },
    );
  }

  public getSchemas(): Observable<CampaignSearchSchema[]> {
    return this.http.get<CampaignSearchSchema[]>(`${this.environmentFacade.getEnvironment()}/rest/v1/loyalty/schemas`, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  public getStaticData(): Observable<StaticData> {
    return this.http.get<StaticData>(`${this.environmentFacade.getEnvironment()}/rest/v1/campaigns/enums`, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  public getCountries(): Observable<Country[]> {
    const url = this.environmentFacade.getEnvironment() + '/rest/v1/countries';
    return this.http.get<Country[]>(url, {
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  public search(): Observable<QcResponseGrid> {
    const payload = this.constructSearchObject();
    const params = new HttpParams()
      .append('start', this.paginatorFacade.pageIndex * this.paginatorFacade.itemsNumber)
      .append('pageSize', this.paginatorFacade.itemsNumber)
      .append(
        'sortField',
        `${
          this.paginatorFacade.selectedSortField === this.campaignLiterals.campaignId
            ? 'CAMPAIGN_ID'
            : this.paginatorFacade.selectedSortField === this.campaignLiterals.campaignName
              ? 'CAMPAIGN_NAME'
              : this.paginatorFacade.selectedSortField === this.campaignLiterals.campaignCategory
                ? 'CAMPAIGN_CATEGORY'
                : this.paginatorFacade.selectedSortField === this.campaignLiterals.campaignActiveFrom
                  ? 'CREATION_DATE'
                  : null
        }`,
      )
      .append(
        'sortOrder',
        `${this.paginatorFacade.selectedOrder === this.paginatorLiterals.orderAsc ? 'ASC' : 'DESC'}`,
      );
    return this.http.post<QcResponseGrid>(
      `${this.environmentFacade.getEnvironment()}/rest/v1/campaigns/byFilter`,
      payload,
      {
        withCredentials: true,
        context: new HttpContext().set(IS_SEARCH, true),
        params,
      },
    );
  }

  public getCount(): Observable<any> {
    const payload = this.constructSearchObject();
    return this.http.post<any>(`${this.environmentFacade.getEnvironment()}/rest/v1/campaigns/countByFilter`, payload, {
      withCredentials: true,
    });
  }

  public sendCampaign(): Observable<{}> {
    const params = new HttpParams().append('campaignId', this.facade.selectedCampaign.campaignId);
    const url = this.environmentFacade.getEnvironment() + '/rest/v1/campaigns/executions/adhoc-run';
    return this.http.put(url, null, { params, withCredentials: true, context: new HttpContext().set(IS_LOAD, true) });
  }

  public copyCampaign(): Observable<any> {
    const params = new HttpParams()
      .append('campaignId', this.facade.selectedCampaign.campaignId)
      .append('campaignName', this.facade.duplicateCampaignName)
      .append('deactivate', this.facade.originalCampaignDeactivation);
    const url = this.environmentFacade.getEnvironment() + '/rest/v1/campaigns/copy';

    return this.http.post<any>(url, null, {
      params,
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  public toggleCampaignActivation(): Observable<any> {
    const params = new HttpParams()
      .append('campaignId', this.facade.selectedCampaign.campaignId)
      .append('deactivate', this.facade.campaignDeactivation);
    const url = this.environmentFacade.getEnvironment() + '/rest/v1/campaigns/activation';

    return this.http.put<any>(url, null, {
      params,
      withCredentials: true,
      context: new HttpContext().set(IS_LOAD, true),
    });
  }

  private constructSearchObject() {
    const payload = {
      activeDateFromStartDate: this.dateHandler(this.facade.activeFromStartDate),
      activeDateFromEndDate: this.dateHandler(this.facade.activeFromEndDate),
      activeDateUntilEndDate: this.dateHandler(this.facade.activeUntilEndDate),
      activeDateUntilStartDate: this.dateHandler(this.facade.activeUntilStartDate),
      campaignCategoryId: this.facade.category || null,
      campaignName: this.facade.name || null,
      campaignSelectionTarget: this.facade.targetGroup || null,
      campaignChannel: this.facade.channel || null,
      campaignStatus: this.facade.status || null,
      deactivate: this.facade.activeCampaigns || null,
      deleted: this.facade.deletedCampaigns || null,
      description: this.facade.description || null,
      loyaltySchemaId: this.facade.schema || null,
      notes: this.facade.notes || null,
      user: this.facade.myCampaigns || null,
    };
    return payload;
  }

  private dateHandler(date: Date) {
    if (!isValid(date) || isNaN(Number(date))) {
      return null;
    }

    return format(parseISO(date.toISOString()), "yyyy-MM-dd'T'HH:mm:ss.SSSxx");
  }
}
